import { Inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import * as actions from 'src/app/recruiting/jobs/details/components/store/actions/job-details.actions';
import { RecruitingService } from 'src/app/recruiting/recruiting.service';
import { ToasterService } from 'src/app/shared/services/toaster.service';
import { APP_CONFIG, Settings } from 'src/app/config/settings';
import { UntilDestroy } from '@ngneat/until-destroy';

@UntilDestroy()
@Injectable()
export class JobDetailsEffects {
    constructor(
        private readonly actions$: Actions,
        protected readonly recruitingService: RecruitingService,
        private readonly toaster: ToasterService,
        @Inject(APP_CONFIG) private readonly _settings: Settings
    ) {}

    handleError(error) {
        return of(actions.loadJobDetailsFailed({ error }));
    }

    getJobDetails$ = createEffect(() =>
        this.actions$.pipe(
            ofType(actions.loadJobDetails),
            switchMap((action) => {
                return this.recruitingService.getJobDetailsById(action.jobId).pipe(
                    map((response) => {
                        const job = response;

                        if (job.facilityAddress && job.city && job.stateName) {
                            const googleMapsKey = this._settings.googleMapsKey;

                            job.googlemapssearcher = `https://www.google.com/maps/embed/v1/place?key=${googleMapsKey}&q=${job.facilityAddress
                                .split(' ')
                                .join('+')}${job.city.split(' ').join('+')}${job.stateName
                                .split(' ')
                                .join('+')}&zoom=11`;
                        }

                        job.shiftType = `(${job.shiftDuration}${(job.shiftName || '').charAt(0)})`;
                        // a bug in the DB view nurses.AyaPortal.JobGridItems does not return a value when the Alt shift name should be "Night"
                        const altShiftName = job.shiftAltName || '';
                        job.altShiftType = `(${job.shiftAltDuration}${
                            altShiftName.length > 1 ? altShiftName.charAt(0) : 'N'
                        })`;

                        return actions.loadWeeklyRates({ job });
                    }),
                    catchError(this.handleError)
                );
            })
        )
    );

    getJobWeeklyrates$ = createEffect(() =>
        this.actions$.pipe(
            ofType(actions.loadWeeklyRates),
            switchMap((action) => {
                return this.recruitingService.getWeeklyPayRates(action.job.id).pipe(
                    map((response) => {
                        const job = { ...action.job };
                        const rates = response;

                        if (rates) {
                            job.weeklyPayLow = rates.weeklyPayLow;
                            job.weeklyPayHigh = rates.weeklyPayHigh;
                            job.altWeeklyPayLow = rates.altWeeklyPayLow;
                            job.altWeeklyPayHigh = rates.altWeeklyPayHigh;
                            job.hospitalTypeId = rates.hospitalTypeId;
                        }

                        if (job.hospitalTypeId === 1) {
                            return actions.loadPerDiemRates({ job });
                        }

                        return actions.loadJobDetailsSuccess({ job });
                    }),
                    catchError(this.handleError)
                );
            })
        )
    );

    getJobPerDiemRates$ = createEffect(() =>
        this.actions$.pipe(
            ofType(actions.loadPerDiemRates),
            switchMap((action) => {
                return this.recruitingService.getPerDiemPayRate(action.job.id).pipe(
                    map((response) => {
                        const job = { ...action.job };
                        const rates = response;

                        const customRate = rates && rates?.customRate !== 0 ? rates.customRate : 0;
                        const facilityRate = rates && rates?.facilityRate !== 0 ? rates.facilityRate : 0;

                        job.perDiemPayRate = customRate > 0 ? customRate : facilityRate;

                        return actions.loadJobDetailsSuccess({ job });
                    }),
                    catchError(this.handleError)
                );
            })
        )
    );

    getJobDetailsFails$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(actions.loadJobDetailsFailed),
                tap((action) => {
                    console.log(action.error);
                    this.toaster.toast('Sorry. A problem occurred while loading Job Details.');
                })
            ),
        { dispatch: false }
    );
}
